import { styled } from "goober";
import type { ComponentProps } from "react";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";

export type ChatAction = {
  icon: ComponentProps<typeof Icon>["name"];
  label: string;
  onClick: () => void;
};

type ChatActionsInput = {
  onFork?: (() => void) | undefined;
  onDownload?: (() => void) | undefined;
  onClose?: (() => void) | undefined;
  onTogglePin?: (() => void) | undefined;
  pinned?: boolean;
};

export function chatActionGroups({
  onFork,
  onDownload,
  onClose,
  onTogglePin,
  pinned,
}: ChatActionsInput): { primary: ChatAction[]; secondary: ChatAction[] } {
  const primary: ChatAction[] = [];
  if (onTogglePin) {
    primary.push({
      icon: pinned ? "pin-filled" : "pin",
      label: pinned ? "Unpin chat" : "Pin chat",
      onClick: onTogglePin,
    });
  }
  if (onFork) {
    primary.push({ icon: "copy", label: "Duplicate chat", onClick: onFork });
  }
  if (onDownload) {
    primary.push({
      icon: "circle-arrow-down",
      label: "Download chat",
      onClick: onDownload,
    });
  }
  const secondary: ChatAction[] = [];
  if (onClose) {
    secondary.push({ icon: "x", label: "Close chat", onClick: onClose });
  }
  return { primary, secondary };
}

const Opener = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  color: var(--text-primary);
  cursor: pointer;
  &:hover {
    background: var(--alpha4);
  }
`;

export function ChatActionsMenu(props: ChatActionsInput) {
  const { primary, secondary } = chatActionGroups(props);
  if (primary.length === 0 && secondary.length === 0) return null;

  const toItems = (actions: ChatAction[]) =>
    actions.map((a) => ({
      type: "button" as const,
      left: <Icon name={a.icon} size={16} />,
      label: a.label,
      onClick: a.onClick,
    }));

  return (
    <Dropdown
      align="bottom"
      margin={6}
      minWidth={180}
      opener={
        <Opener aria-label="Chat actions">
          <Icon name="more" size={12} />
        </Opener>
      }
      sections={[
        ...(primary.length > 0 ? [{ items: toItems(primary) }] : []),
        ...(secondary.length > 0 ? [{ items: toItems(secondary) }] : []),
      ]}
    />
  );
}
